import { useRef, useState } from "react";
import { Download, Loader2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  confirmImport,
  downloadImportTemplate,
  fetchImportResult,
  previewImport,
  type ImportLine,
  type ImportPreview,
  type ImportResult,
} from "@/lib/adminCostImportApi";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const money = (v: number | null) => (v == null ? "—" : brl.format(v));
const pct = (v: number | null) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v.toFixed(2))}%`);
const selectable = (l: ImportLine) => l.status === "VALID" || l.status === "WARN_DATA_ANTIGA";

export function CostImportDialog({
  open,
  basePath,
  templateFilename,
  title,
  canEdit,
  onClose,
  onApplied,
}: {
  open: boolean;
  basePath: string;
  templateFilename: string;
  title: string;
  canEdit: boolean;
  onClose: () => void;
  onApplied: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [acks, setAcks] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ImportResult | null>(null);
  const [lookupId, setLookupId] = useState("");

  const reset = () => { setPreview(null); setSelected(new Set()); setAcks(new Set()); setResult(null); setLookupId(""); };

  const close = () => { reset(); onClose(); };

  const onFile = async (file: File) => {
    setBusy(true);
    setResult(null);
    try {
      const p = await previewImport(basePath, file);
      setPreview(p);
      setSelected(new Set(p.lines.filter(selectable).map((l) => l.id)));
      setAcks(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao processar o arquivo");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      await downloadImportTemplate(basePath, templateFilename);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível baixar o modelo");
    } finally {
      setBusy(false);
    }
  };

  const lookup = async () => {
    const id = lookupId.trim();
    if (!id) return;
    setBusy(true);
    try {
      const res = await fetchImportResult(basePath, id);
      // Reconstitui a visão (tabela + banner de resultado) a partir do resultado persistido.
      const summary = {
        total: res.lines.length,
        valid: res.lines.filter((l) => l.status === "VALID").length,
        warning: res.lines.filter((l) => l.status === "WARN_DATA_ANTIGA").length,
        ignored: res.lines.filter((l) => l.status === "IGNORED").length,
        error: res.lines.filter((l) => l.status === "ERRO").length,
      };
      setPreview({ batchId: res.batchId, domain: "", status: res.status, expiresAt: null, summary, fileErrors: [], lines: res.lines });
      setResult(res);
      setSelected(new Set());
      setAcks(new Set());
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
        toast.error("Importação não encontrada para este usuário.");
      } else {
        toast.error(e instanceof Error ? e.message : "Falha ao consultar a importação");
      }
    } finally {
      setBusy(false);
    }
  };

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  };

  const selectedWarnMissingAck = preview
    ? preview.lines.some((l) => l.status === "WARN_DATA_ANTIGA" && selected.has(l.id) && !acks.has(l.id))
    : false;

  const confirm = async () => {
    if (!preview?.batchId) return;
    setBusy(true);
    try {
      const res = await confirmImport(basePath, preview.batchId, {
        selectedLineIds: [...selected],
        acknowledgedOldDateLineIds: [...acks].filter((id) => selected.has(id)),
        idempotencyKey: crypto.randomUUID(),
      });
      setResult(res);
      toast.success(`Importação aplicada: ${res.appliedCount} linha(s)`);
      onApplied();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error(e.message || "Conflito — gere uma nova prévia.");
      } else {
        toast.error(e instanceof Error ? e.message : "Falha ao confirmar a importação");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={busy}
              onClick={() => void downloadTemplate()}>
              <Download className="h-4 w-4" /> Baixar modelo
            </Button>
            <input ref={fileRef} type="file" className="hidden"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ""; }} />
            <Button type="button" variant="outline" size="sm" disabled={busy}
              onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar XLSX
            </Button>
            {preview?.expiresAt && !result && (
              <span className="text-[11px] text-muted-foreground">
                Prévia válida até {new Date(preview.expiresAt).toLocaleTimeString("pt-BR")}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="Consultar importação por código (batchId)"
              className="h-8 max-w-xs text-xs"
            />
            <Button type="button" variant="outline" size="sm" disabled={busy || !lookupId.trim()}
              onClick={() => void lookup()}>
              <Search className="h-4 w-4" /> Consultar
            </Button>
          </div>

          {preview && preview.fileErrors.length > 0 && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              <p className="font-medium">Arquivo rejeitado:</p>
              <ul className="mt-1 list-inside list-disc">{preview.fileErrors.map((er, i) => <li key={i}>{er}</li>)}</ul>
            </div>
          )}

          {preview && preview.batchId && (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge label="Total" value={preview.summary.total} />
                <Badge label="Válidas" value={preview.summary.valid} />
                <Badge label="Avisos" value={preview.summary.warning} />
                <Badge label="Ignoradas" value={preview.summary.ignored} />
                <Badge label="Erros" value={preview.summary.error} />
              </div>

              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/40 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left">Sel.</th>
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Identificador / Nome</th>
                      <th className="p-2 text-right">Atual → Novo</th>
                      <th className="p-2 text-right">Var.</th>
                      <th className="p-2 text-left">Unid.</th>
                      <th className="p-2 text-left">Data</th>
                      <th className="p-2 text-left">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.lines.map((l) => (
                      <tr key={l.id} className={cn("border-t border-border", l.status === "ERRO" && "bg-destructive/5")}>
                        <td className="p-2">
                          <input type="checkbox" disabled={!selectable(l) || !!result || !canEdit}
                            checked={selected.has(l.id)} onChange={() => setSelected((s) => toggle(s, l.id))} />
                        </td>
                        <td className="p-2 tabular-nums">{l.lineNumber}</td>
                        <td className="p-2">
                          <div className="font-medium">{l.targetRef}</div>
                          <div className="text-muted-foreground">{l.targetName ?? "—"}</div>
                        </td>
                        <td className="p-2 text-right tabular-nums">{money(l.currentCost)} → {money(l.newCost)}</td>
                        <td className="p-2 text-right tabular-nums">{pct(l.variationPercent)}</td>
                        <td className="p-2">{l.unitLabel ?? "—"}</td>
                        <td className="p-2">{l.referenceDate ?? "—"}</td>
                        <td className="p-2">
                          <StatusTag line={l} />
                          {l.message && <div className="text-[11px] text-muted-foreground">{l.message}</div>}
                          {l.status === "WARN_DATA_ANTIGA" && selected.has(l.id) && !result && (
                            <label className="mt-1 flex items-center gap-1 text-[11px] text-warning">
                              <input type="checkbox" checked={acks.has(l.id)}
                                onChange={() => setAcks((s) => toggle(s, l.id))} disabled={!canEdit} />
                              Reconhecer data antiga
                            </label>
                          )}
                          {result && l.applyResult === "APPLIED" && (
                            <div className="text-[11px] font-medium text-primary">aplicada</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!result && canEdit && (
                <div className="flex items-center justify-end gap-2">
                  {selectedWarnMissingAck && (
                    <span className="text-[11px] text-warning">Reconheça os avisos de data antiga das linhas selecionadas.</span>
                  )}
                  <Button type="button" variant="hero" disabled={busy || selected.size === 0 || selectedWarnMissingAck}
                    onClick={() => void confirm()}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Confirmar {selected.size} linha(s)
                  </Button>
                </div>
              )}
              {result && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                  Importação <strong>{result.status}</strong> — {result.appliedCount} linha(s) aplicada(s)
                  {result.appliedAt && ` em ${new Date(result.appliedAt).toLocaleString("pt-BR")}`}.
                  <div className="mt-1 break-all text-[11px] text-muted-foreground">Código: {result.batchId}</div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Badge({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded-md border border-border bg-surface px-2 py-1">
      {label}: <strong className="tabular-nums">{value}</strong>
    </span>
  );
}

function StatusTag({ line }: { line: ImportLine }) {
  const map: Record<ImportLine["status"], string> = {
    VALID: "bg-primary/10 text-primary",
    WARN_DATA_ANTIGA: "bg-warning/15 text-warning",
    IGNORED: "bg-secondary text-muted-foreground",
    ERRO: "bg-destructive/15 text-destructive",
  };
  const txt: Record<ImportLine["status"], string> = {
    VALID: "Válida", WARN_DATA_ANTIGA: "Aviso", IGNORED: "Ignorada", ERRO: "Erro",
  };
  return <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", map[line.status])}>{txt[line.status]}</span>;
}
